import React from "react";
import { Repeat, Smartphone, Tablet, Monitor, Clock, Building2, Target, AlertTriangle, Linkedin } from "lucide-react";

// Phase 25.13ad — Shared metadata strip shown under a session's landing
// path. Renders returning-visitor badge, device hint, first-seen-ever
// timestamp, and ASN/org name (Phase 25.13ae). Used by Country / Referrer
// drawers + HotSessions panel.

function relativeFromNow(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(1, Math.floor((now - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  return `${Math.floor(diffSec / (86400 * 30))}mo ago`;
}

// Phase 25.13ae — collapse generic carrier orgs (Comcast, Verizon, Telstra…)
// since they're noise. Highlight only orgs that look like enterprise /
// cloud / corporate networks because those are the high-signal cases.
const CONSUMER_ASN_HINTS = [
  "telstra", "optus", "vodafone", "comcast", "verizon", "at&t",
  "spectrum", "cox", "charter", "rogers", "bell", "telus",
  "singtel mobile", "starhub mobile", "m1 mobile", "starhub net",
  "british telecom retail", "sky uk", "bt group plc",
  "deutsche telekom", "swisscom", "orange s.a.", "movistar",
  "free sas", "claro", "tim brasil", "telcel", "true online",
  "airtel", "jio", "reliance", "bsnl",
];

function compactOrg(rawOrg) {
  if (!rawOrg) return "";
  // ipapi.co usually returns "ASNNNN OrgName". Strip the AS prefix for UI.
  const m = rawOrg.match(/^AS\d+\s+(.+)$/i);
  return (m ? m[1] : rawOrg).trim().slice(0, 60);
}

function isCorporate(rawOrg) {
  if (!rawOrg) return false;
  const lower = rawOrg.toLowerCase();
  return !CONSUMER_ASN_HINTS.some((token) => lower.includes(token));
}

export default function SessionMetaRow({ session }) {
  if (!session) return null;
  const ret = session.returning || {};
  const device = session.device || {};
  const orgRaw = session.landing_asn_org || "";
  const org = compactOrg(orgRaw);
  const orgCorporate = isCorporate(orgRaw);
  const asnCity = session.landing_asn_city || "";

  // Device icon + label
  let DeviceIcon = Monitor;
  if (device.device === "mobile") DeviceIcon = Smartphone;
  else if (device.device === "tablet") DeviceIcon = Tablet;

  return (
    <div className="flex items-center gap-3 flex-wrap mt-1.5 font-mono text-[10px] text-zinc-500">
      {/* Phase 25.13ao · P4 — LinkedIn-attributed session badge. Shown
          when any pageview in this session came from a LinkedIn referrer
          (web or mobile app). Lets the operator distinguish active-
          outreach traffic from random direct loads. */}
      {(session.linkedin_attributed || session.from_linkedin) && (
        <span
          className="text-[#0A66C2] border border-[#0A66C2]/50 bg-[#0A66C2]/10 px-1.5 py-px uppercase tracking-widest flex items-center gap-1 font-bold"
          title="This session landed from a LinkedIn referrer. Likely from your active outreach (DMs / posts). Engagement +4."
          data-testid="session-linkedin-badge"
        >
          <Linkedin className="w-2.5 h-2.5" />
          VIA LINKEDIN
        </span>
      )}
      {session.watchlist_match && (() => {
        const wm = session.watchlist_match;
        const transition = wm.transition_status;
        const transitionBadge = transition === "interim"
          ? { label: "INTERIM CEO · SOFT-TOUCH", cls: "text-[#FFB000] border-[#FFB000]/50 bg-[#FFB000]/10" }
          : transition === "outgoing"
          ? { label: "CEO OUTGOING", cls: "text-[#FF6B47] border-[#FF6B47]/50 bg-[#FF6B47]/10" }
          : transition === "new_ceo"
          ? { label: "NEW CEO <90D", cls: "text-[#00E676] border-[#00E676]/50 bg-[#00E676]/10" }
          : null;
        const tip = [
          `Watchlist match · category: ${wm.category || "—"}. Engagement +10.`,
          wm.current_ceo ? `Current CEO: ${wm.current_ceo}` : null,
          wm.outgoing_ceo ? `Outgoing: ${wm.outgoing_ceo}` : null,
          wm.note ? `Note: ${wm.note}` : null,
        ].filter(Boolean).join("\n");
        return (
          <>
            <span
              className="text-[#FF3B30] border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-1.5 py-px uppercase tracking-widest flex items-center gap-1 font-bold"
              title={tip}
              data-testid="session-watchlist-badge"
            >
              <Target className="w-2.5 h-2.5" />
              WATCHLIST · {wm.name.toUpperCase()}
            </span>
            {transitionBadge && (
              <span
                className={`border px-1.5 py-px uppercase tracking-widest flex items-center gap-1 font-bold ${transitionBadge.cls}`}
                title={wm.note || tip}
                data-testid="session-watchlist-transition"
              >
                <AlertTriangle className="w-2.5 h-2.5" />
                {transitionBadge.label}
              </span>
            )}
          </>
        );
      })()}
      {ret.is_returning && (
        <span
          className="text-[#FFB000] border border-[#FFB000]/40 bg-[#FFB000]/10 px-1.5 py-px uppercase tracking-widest flex items-center gap-1"
          title={`Returning visitor — ${ret.lifetime_pv ?? 0} lifetime pageviews · first seen ${relativeFromNow(ret.first_seen_ever)}`}
          data-testid="session-returning-badge"
        >
          <Repeat className="w-2.5 h-2.5" />
          RETURNING · {ret.lifetime_days ?? 1}D · {ret.lifetime_pv ?? 0}PV
        </span>
      )}
      {org && (
        <span
          className={`flex items-center gap-1 uppercase tracking-widest border px-1.5 py-px ${
            orgCorporate
              ? "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5"
              : "text-zinc-500 border-zinc-800 bg-zinc-950/40"
          }`}
          title={`Network: ${orgRaw}${asnCity ? ` · ${asnCity}` : ""}${orgCorporate ? " · Looks corporate / non-residential" : " · Residential / consumer ISP"}`}
          data-testid="session-asn-badge"
        >
          <Building2 className="w-2.5 h-2.5" />
          {org}{asnCity ? ` · ${asnCity}` : ""}
        </span>
      )}
      {device.label && (
        <span className="flex items-center gap-1 uppercase tracking-widest" title={`User-agent fingerprint: ${device.os}`}>
          <DeviceIcon className="w-2.5 h-2.5" />
          {device.label}
        </span>
      )}
      {ret.first_seen_ever && (
        <span className="flex items-center gap-1 uppercase tracking-widest text-zinc-600" title={`First-ever visit (90-day retention): ${ret.first_seen_ever}`}>
          <Clock className="w-2.5 h-2.5" />
          1ST SEEN {relativeFromNow(ret.first_seen_ever)}
        </span>
      )}
    </div>
  );
}
